import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { IconLoading, IconWarning } from 'src/easyclaim.app.assets';
import CustomerConfigurationContext from 'src/easyclaim.app.contexts/CustomerConfigurationContextCore';
import styled, { css } from 'styled-components';

import {
	DocumentAddCommandResult,
	ValidationResult,
} from '../../easyclaim.api.client.typescript/src/index';
import theme from '../../easyclaim.app.theme/theme';
import { FadeIn } from '../animations/FadeIn';

import { Circle } from './Circle';
import { StyledIconCheck, StyledIconConnectionError, StyledIconQuestionMark } from './CommonStyles';
import { pictureRejectionDocumentFeatures } from './PictureRejection';
import { Spinner } from './Spinner';

export interface StatusIconProps {
	isUploading: boolean;
	uploadResult?: DocumentAddCommandResult;
}

const StatusIcon: React.FC<StatusIconProps> = ({ isUploading, uploadResult }) => {
	const customerConfigurationContext = useContext(CustomerConfigurationContext).configuration;
	const useSolvdStyles = !!customerConfigurationContext?.useSolvdStyles;

	const treatErrorAsWarning =
		uploadResult &&
		uploadResult.validationResults &&
		uploadResult.validationResults.some((vr) => vr.validationResult === ValidationResult.Error) &&
		uploadResult.validationResults.some((vr) =>
			pictureRejectionDocumentFeatures.some((e) => e === vr.feature)
		);

	const treatAsError =
		!uploadResult ||
		!uploadResult.validationResults ||
		(!treatErrorAsWarning &&
			uploadResult.validationResults.some((vr) => vr.validationResult === ValidationResult.Error));

	const treatAsWarning =
		!treatAsError &&
		uploadResult.validationResults!.some((vr) => vr.validationResult === ValidationResult.Warning);

	const color = treatAsError
		? theme.colors.danger
		: treatAsWarning || treatErrorAsWarning
			? theme.colors.warning
			: theme.colors.success;

	const className = treatAsError
		? 'ErrorCircle'
		: treatAsWarning || treatErrorAsWarning
			? 'WarningCircle'
			: 'SuccessCircle';

	const icon = treatAsError ? (
		<StyledIconConnectionError className="ErrorIcon" />
	) : treatAsWarning || treatErrorAsWarning ? (
		<StyledIconQuestionMark className="WarningIcon" />
	) : (
		<StyledIconCheck className="SuccessIcon" />
	);

	return isUploading ? (
		<CenteredContainer>
			{useSolvdStyles ? (
				<div className="solvdLoadingContainer">
					<IconLoading className="solvdLoading" />
					<h2>{<FormattedMessage id="photoUploading.title" />}</h2>
				</div>
			) : (
				<Spinner dotStyle={{ size: '.75rem', margin: '0.5rem' }} />
			)}
		</CenteredContainer>
	) : (
		<>
			{treatAsError ? (
				<CenteredContainer className="selectImage-error-container">
					<StyledErrorTriangleIcon className="ErrorTriangleIcon" />
				</CenteredContainer>
			) : undefined}
			<BottomCircle
				className={className}
				borderColor={color}
				borderWidth="1px"
				borderStyle="solid"
				backgroundColor="white"
			>
				{icon}
			</BottomCircle>
		</>
	);
};

export default StatusIcon;

const BottomCircle = styled(Circle)`
	position: absolute;
	height: 1rem;
	bottom: 1rem;
	right: 1rem;
	opacity: 0;
	animation: ${css`
			${FadeIn}`} 0.3s ease-out forwards 0.9s;
`;

export const CenteredContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 999;
`;

const StyledErrorTriangleIcon = styled(IconWarning)`
	color: ${(p) => p.theme.colors.danger};
	vertical-align: middle;
	height: 2rem;
	animation: ${css`
			${FadeIn}`} 0.3s ease-in forwards;
`;
